import { i18n } from '@marine/i18n';
import type { CommonPowerSyncDatabase, PowerSyncBackendConnector } from '@powersync/common';
import { connectVerified } from './connectVerified';
import { wipeLocalData } from './localDataWipe';
import { powersyncLogger } from './logger';
import type { SyncIdentityStore } from './syncIdentity';
import { recordSyncIdentity } from './syncIdentity';

export const ORG_SWITCH_PHASE = {
  idle: 'idle',
  /** Waiting for changes made in the current organization to reach the server. */
  uploading: 'uploading',
  /** Clerk is switching, then the local database is wiped and reconnected. */
  switching: 'switching',
  /** Connected as the new organization, waiting for its core data. */
  syncing: 'syncing',
} as const;

export type OrgSwitchPhase = (typeof ORG_SWITCH_PHASE)[keyof typeof ORG_SWITCH_PHASE];

export const ORG_SWITCH_FAILURE = {
  /** There are unsent changes and no connection to send them over. */
  offline: 'offline',
  /** There are unsent changes and they did not finish uploading in time. */
  uploadTimeout: 'upload_timeout',
} as const;

export type OrgSwitchFailureReason = (typeof ORG_SWITCH_FAILURE)[keyof typeof ORG_SWITCH_FAILURE];

/** A switch that was refused before anything changed: Clerk still names the old organization. */
export class OrgSwitchError extends Error {
  readonly reason: OrgSwitchFailureReason;

  constructor(reason: OrgSwitchFailureReason, message: string) {
    super(message);
    this.name = 'OrgSwitchError';
    this.reason = reason;
  }
}

/** What to tell the user when `switchTo` or `uploadPendingChanges` rejects. Shared by both apps. */
export const describeOrgSwitchFailure = (error: unknown): string => {
  if (error instanceof OrgSwitchError) {
    return error.reason === ORG_SWITCH_FAILURE.offline
      ? i18n.t('organizations:switch.failure.offline')
      : i18n.t('organizations:switch.failure.uploading');
  }

  return i18n.t('organizations:switch.failure.generic');
};

/**
 * The organization to make active for a user whose session names none, or `null` to leave it alone.
 *
 * Clerk leaves a session without an active organization unless the instance requires membership,
 * and production does not: every session made before the apps knew about organizations has none,
 * and nothing in either app ever picked one. Everything org-scoped then fails closed — the switcher
 * hides, the owner's gates answer no, the plan never loads.
 *
 * The first membership wins, in the order Clerk lists them. Almost every user has exactly one, and a
 * user with several can switch from there. `undefined` is "not loaded yet", and decides nothing.
 */
export const pickOrganizationToActivate = (membershipOrgIds: readonly string[] | undefined): string | null =>
  membershipOrgIds?.[0] ?? null;

/**
 * The `core` tier in `sync-streams.yaml` — boats, models, subscriptions, locations. Enough to render
 * the app shell, so the blocking state lifts there and the heavier tiers keep streaming in behind it.
 */
const CORE_SYNC_PRIORITY = 1;

const UPLOAD_TIMEOUT_MS = 30_000;
const UPLOAD_POLL_MS = 250;
const CORE_SYNC_TIMEOUT_MS = 60_000;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type DrainUploadQueueOptions = {
  database: CommonPowerSyncDatabase;
  timeoutMs?: number;
  pollMs?: number;
};

/**
 * Resolves once every locally made change has been uploaded, or throws `OrgSwitchError`.
 *
 * Has to finish before `setActive`. The upload queue is sent with whatever token Clerk hands out at
 * the time, and the backend stamps `organization_id` from that token — so a change made in one
 * organization but uploaded after the switch is filed under the other. Wiping instead would discard
 * it, since `disconnectAndClear` clears the queue along with the tables.
 */
export const drainUploadQueue = async ({
  database,
  timeoutMs = UPLOAD_TIMEOUT_MS,
  pollMs = UPLOAD_POLL_MS,
}: DrainUploadQueueOptions): Promise<void> => {
  let { count } = await database.getUploadQueueStats();

  if (count === 0) {
    return;
  }

  powersyncLogger.info('uploading pending changes before switching organization', { count });

  const deadline = Date.now() + timeoutMs;

  while (count > 0) {
    const { connected, connecting } = database.currentStatus;

    if (!connected && !connecting) {
      throw new OrgSwitchError(
        ORG_SWITCH_FAILURE.offline,
        `${count} changes are waiting to upload, and there is no connection`,
      );
    }

    if (Date.now() >= deadline) {
      throw new OrgSwitchError(ORG_SWITCH_FAILURE.uploadTimeout, `${count} changes were still waiting to upload`);
    }

    await delay(pollMs);
    ({ count } = await database.getUploadQueueStats());
  }
};

type ResyncForOrganizationOptions = {
  database: CommonPowerSyncDatabase;
  connector: PowerSyncBackendConnector;
  /** Reported to PowerSync as connection metadata. */
  appVersion: string;
  store: SyncIdentityStore;
  userId: string;
  orgId: string;
  onPhase: (phase: OrgSwitchPhase) => void;
  coreSyncTimeoutMs?: number;
};

/**
 * Replaces the local database with `orgId`'s data. Clerk must already name `orgId` as active, so the
 * reconnect fetches a token scoped to it.
 *
 * Resolves once the core tier has synced, or after `coreSyncTimeoutMs` on a slow connection — the
 * sync carries on in the background either way, and holding the whole app behind a spinner on a bad
 * network is worse than an app that fills in as data arrives.
 */
export const resyncForOrganization = async ({
  database,
  connector,
  appVersion,
  store,
  userId,
  orgId,
  onPhase,
  coreSyncTimeoutMs = CORE_SYNC_TIMEOUT_MS,
}: ResyncForOrganizationOptions): Promise<void> => {
  onPhase(ORG_SWITCH_PHASE.switching);

  await wipeLocalData(database);
  // Only after the wipe: recorded any earlier, an app killed mid-wipe would boot believing the
  // leftover rows already belong to the new organization, and keep them.
  recordSyncIdentity({ store, userId, orgId });

  await connectVerified({ database, connector, appVersion, isCancelled: () => false });

  onPhase(ORG_SWITCH_PHASE.syncing);

  const timeout = new AbortController();
  const timer = setTimeout(() => timeout.abort(), coreSyncTimeoutMs);

  try {
    // Resolves on abort too, so whether it synced is read off the status afterwards.
    await database.waitForFirstSync({ priority: CORE_SYNC_PRIORITY, signal: timeout.signal });
  } finally {
    clearTimeout(timer);
  }

  if (!database.currentStatus.statusForPriority(CORE_SYNC_PRIORITY)?.hasSynced) {
    powersyncLogger.warn('organization switch released before its core data synced', {
      timeoutMs: coreSyncTimeoutMs,
    });
  }
};

type SwitchOrganizationOptions = Omit<ResyncForOrganizationOptions, 'onPhase'> & {
  /** Makes `orgId` Clerk's active organization — `setActive({ organization })`. */
  activate: (orgId: string) => Promise<void>;
  onPhase: (phase: OrgSwitchPhase) => void;
  uploadTimeoutMs?: number;
};

/**
 * Switches the active organization and replaces the local data with the new organization's.
 *
 * Throws `OrgSwitchError` without changing anything when pending changes cannot be uploaded first.
 * Anything that fails after `activate` has run leaves the recorded identity naming the old
 * organization, so the next boot's `wipeLocalDataOnIdentityChange` finishes the job.
 */
export const switchOrganization = async ({
  activate,
  onPhase,
  uploadTimeoutMs,
  ...resync
}: SwitchOrganizationOptions): Promise<void> => {
  onPhase(ORG_SWITCH_PHASE.uploading);
  await drainUploadQueue({ database: resync.database, timeoutMs: uploadTimeoutMs });

  onPhase(ORG_SWITCH_PHASE.switching);
  powersyncLogger.info('switching organization');
  await activate(resync.orgId);

  await resyncForOrganization({ ...resync, onPhase });
  powersyncLogger.info('organization switch finished');
};
